import React, { useState, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { Checkbox } from 'ui-components/components/checkbox';
import { Text, TextType } from 'ui-components/components/text';

import messages from './messages';
import HelpArticleLink from '../../../../components/help-article-link';
import { STAMPS_USPSFIRSTCLASS_SERVICE_ID, ENDICIA_USPSFIRSTCLASS_SERVICE_ID } from '../../../../constants/services';
import { SellerSettings } from '../../../../enums/seller-settings/seller-settings';
import {
  useReturnService,
  useReturnServiceCollectionGuid,
  useReturnPackage,
  useReturnTracking,
  useIsShipstationReturnsEnabled,
} from '../../../../hooks/use-seller-setting';
import Global from '../../../../store/global-selectors';
import { saveNewSellerSettings } from '../../../../store/seller/seller.actions';
import { updateSellerSettingsBulk } from '../../api/save-settings';
import SaveBar from '../../components/save-bar';
import commonStyles from '../../settings-page.module.less';
import PackageDropdownContainer from './package-dropdown/container';
import style from './style.module.less';
import ShippingServiceSelector from '../../../../components/shipping-service-selector';
import useCarrierGroups from '../../../../components/shipping-service-selector/group-hooks/use-carrier-groups';
import useProviderGroups from '../../../../components/shipping-service-selector/group-hooks/use-provider-groups';
import useServiceCollectionGroup from '../../../../components/shipping-service-selector/group-hooks/use-service-collection-group';
import useServicesFromPrimaryProviders from '../../../../components/shipping-service-selector/service-id-hooks/use-services-from-primary-providers';
import useSelectedItemName from '../../../../components/shipping-service-selector/use-selected-item-name';
import { localDeliveryBlacklistFilterCB } from '../../../../fulfillment-planner/context/hooks';
import { ACCESS_LEVELS, useRequiresRateShopperAccessLevel } from '../../../../hooks/use-has-rate-shopper-access-level';
import { linkIDs } from '../../../../services/link-service/links';
import { SettingsPageTitle } from '../../components/settings-page-title/settings-page-title';

const getDomesticServicesOnly = (service) => {
  const isDomesticService = service.features?.supportsInternationalShipping === false;
  return isDomesticService;
};
const availableServiceFilters = [getDomesticServicesOnly, localDeliveryBlacklistFilterCB];

const USPSFirstClassMailServiceIds = [STAMPS_USPSFIRSTCLASS_SERVICE_ID, ENDICIA_USPSFIRSTCLASS_SERVICE_ID];

export const Returns = () => {
  const dispatch = useDispatch();
  const isShipstationReturnsEnabled = useIsShipstationReturnsEnabled();
  const allServices = useSelector(Global.selectSellerServices);
  const availableServices = useMemo(
    () => allServices.filter((service) => availableServiceFilters.every((filter) => filter(service))),
    [allServices],
  );

  const primaryProviderServices = useServicesFromPrimaryProviders();
  const filteredPrimaryServices = useMemo(() => primaryProviderServices.filter(localDeliveryBlacklistFilterCB), [primaryProviderServices]);

  const serviceId = useReturnService();
  const initialServiceCollectionGuid = useReturnServiceCollectionGuid();

  const [selectedSellerProviderId, setSelectedSellerProviderId] = useState();
  const [selectedServiceId, setSelectedServiceId] = useState(serviceId);
  const [selectedServiceCollectionGuid, setSelectedServiceCollectionGuid] = useState(initialServiceCollectionGuid);
  const initialPackageId = useReturnPackage();
  const [selectedPackageId, setSelectedPackageId] = useState(initialPackageId);
  const initialTrackingOption = useReturnTracking();
  const [uiTrackingOption, setUiTrackingOption] = useState(initialTrackingOption);

  const selectedService = useSelector((state) => Global.selectSellerServiceById(state, { serviceId: selectedServiceId }));

  const toggleTrackingOption = useCallback(() => setUiTrackingOption((prevValue) => !prevValue), []);
  const onSave = useCallback(async () => {
    const sellerSettings = {
      [SellerSettings.returnService.key]: selectedServiceId,
      [SellerSettings.returnServiceCollectionGuid.key]: selectedServiceCollectionGuid ?? '',
      [SellerSettings.returnPackage.key]: selectedPackageId,
      [SellerSettings.returnTracking.key]: uiTrackingOption,
    };
    const newSettings = await updateSellerSettingsBulk(sellerSettings);
    dispatch(saveNewSellerSettings(newSettings));
  }, [dispatch, selectedServiceId, selectedServiceCollectionGuid, selectedPackageId, uiTrackingOption]);

  const isUSPSFirstClassMail = USPSFirstClassMailServiceIds.includes(selectedServiceId);

  const onNewServiceSelect = useCallback((newService) => {
    if (newService.serviceCollectionGuid) {
      setSelectedServiceCollectionGuid(newService.serviceCollectionGuid);
      setSelectedServiceId('');
      setSelectedSellerProviderId('');
    } else {
      setSelectedServiceCollectionGuid(null);
      setSelectedServiceId(newService.serviceId || '');
      setSelectedSellerProviderId(newService.sellerLabelProviderId || '');
    }
  }, []);

  const selectedServiceItem = useMemo(
    () => ({
      serviceId: selectedServiceId,
      sellerLabelProviderId: selectedSellerProviderId,
      serviceCollectionGuid: selectedServiceCollectionGuid ?? null,
    }),
    [selectedServiceId, selectedSellerProviderId, selectedServiceCollectionGuid],
  );

  const selectedItemName = useSelectedItemName(selectedServiceItem);

  const carrierGroups = useCarrierGroups(filteredPrimaryServices, selectedServiceItem);
  const serviceCollectionGroup = useServiceCollectionGroup(selectedServiceItem);
  const showRateShopper = useRequiresRateShopperAccessLevel(ACCESS_LEVELS.FREEMIUM);

  const legacyServiceGroups = useProviderGroups(availableServices, selectedServiceItem);

  const rateShopperServiceGroups = useMemo(() => {
    if (showRateShopper && serviceCollectionGroup) {
      return [serviceCollectionGroup, ...carrierGroups];
    }
    return carrierGroups;
  }, [showRateShopper, serviceCollectionGroup, carrierGroups]);

  const resolvedServiceGroups = isShipstationReturnsEnabled ? rateShopperServiceGroups : legacyServiceGroups;

  return (
    <React.Fragment>
      <div className={commonStyles.content}>
        <SettingsPageTitle value={messages.header} />
        <Text textType={TextType.H3} value={messages.headerContent} />
        <div className={style.content}>
          <div className={style.settings}>
            <div className={style['setting-label']}>
              <FormattedMessage {...messages.serviceDropdownLabel} />
            </div>
            <div className={style['setting-options']}>
              <ShippingServiceSelector
                className={style['service-type-dropdown']}
                defaultEmptyText={SellerSettings.returnService.messageDescriptor}
                onSelected={onNewServiceSelect}
                selectedItemName={selectedItemName}
                serviceGroups={resolvedServiceGroups}
              />
            </div>
            <div className={style['setting-label']}>
              <FormattedMessage {...messages.packageDropdownLabel} />
            </div>
            <div className={style['setting-options']}>
              <PackageDropdownContainer
                carrierId={selectedService?.carrierId}
                onPackageSelected={setSelectedPackageId}
                selectedPackageId={selectedPackageId}
              />
            </div>
          </div>
          <div className={style['helper-text']}>{isUSPSFirstClassMail ? <FormattedMessage {...messages.helperTextForUSPS} /> : null}</div>
          {!isShipstationReturnsEnabled && (
            <div className={style['auto-mark-return-checkbox']}>
              <span>
                <Checkbox
                  checked={uiTrackingOption}
                  label={
                    <FormattedMessage
                      {...SellerSettings.returnTracking.messageDescriptor}
                      values={{
                        a: (chunks) => <HelpArticleLink articleId={linkIDs.TrackShipments}>{chunks}</HelpArticleLink>,
                      }}
                    />
                  }
                  onChange={toggleTrackingOption}
                />
              </span>
            </div>
          )}
        </div>
      </div>
      <SaveBar onSave={onSave} />
    </React.Fragment>
  );
};
